import { KendraConst } from "@amzn/ask-legal-domain";
import { Box, SpaceBetween, Badge, Link } from "@amzn/awsui-components-react";
import { QueryResultItem } from "@aws-sdk/client-kendra";
import * as React from "react";
import { SmartRichTextView } from "../../common/RichTextEditor/SmartRichTextView";
import { DateTimeFormatter } from "../../../utils/date-time-utils";
import { getInstanceIdFromResult } from "../../../utils/search-utils";
import { PageSourceLink } from "./PageSourceLink";
import { SearchInstanceBadge } from "./SearchInstanceBadge";
import { CrossInstanceLink } from "./CrossInstanceLink";

interface SearchResultItemProps {
    item: QueryResultItem;
    currentInstanceId?: string;
    submitClickFeedback: (resultId: string) => void;
}

export const SearchResultItem: React.FC<SearchResultItemProps> = ({
    item,
    currentInstanceId,
    submitClickFeedback
}) => {
    const resultInstanceId = getInstanceIdFromResult(item);
    const itemLink = item.DocumentURI;

    const isGlobalSearch = !currentInstanceId;

    const isLegalOnly = item.DocumentAttributes
        .find(attr => attr.Key === KendraConst.ContainerIngestionKendraAttributes.Custom.LegalOnly)
        ?.Value.StringValue === "true";

    const lastUpdatedDate = DateTimeFormatter.toSimpleDate(
        item.DocumentAttributes
            .find(attr => attr.Key === KendraConst.ContainerIngestionKendraAttributes.Default.LastUpdatedDate)
            ?.Value.StringValue
    );

    const handleClickFeedback = () => submitClickFeedback(item.Id);

    // Document excerpt
    const renderExcerpt = () => (
        <Box margin="n" padding="n">
            <span style={{ color: "grey" }}>{lastUpdatedDate}</span>
            &ensp;{item.DocumentExcerpt.Text}
        </Box>
    );

    // Legal only badge and instance badge
    const renderBadges = () => (
        <SpaceBetween direction="horizontal" size="xs">
            {isLegalOnly && (
                <Box variant="small" color="text-body-secondary" margin={{right: "xs"}} padding="n">
                    <Badge color="green">Legal Only</Badge>
                </Box>
            )}
            {resultInstanceId && (
                <SearchInstanceBadge instanceId={resultInstanceId} />
            )}
        </SpaceBetween>
    );

    // Render for Global search
    if (isGlobalSearch) {
        return (
            <SpaceBetween size="xxxs" direction="vertical" className="search-result-item">
                <Link href={itemLink} fontSize="heading-m" external={false}>
                    <SmartRichTextView content={item.DocumentTitle.Text || "Untitled"} />
                </Link>
                {renderExcerpt()}
                <Box className="page-source-link-container">
                    <Link
                        href={itemLink}
                        target="_blank"
                        external
                        externalIconAriaLabel="Opens in a new tab"
                    >
                        View Page
                    </Link>
                </Box>
                {renderBadges()}
            </SpaceBetween>
        );
    }

    // Instance specific search
    return (
        <SpaceBetween size="xxxs" direction="vertical" className="search-result-item">
            <CrossInstanceLink
                resultItem={item}
                currentInstanceId={currentInstanceId}
                targetUrl={itemLink}
                fontSize="heading-m"
                onClickFeedback={handleClickFeedback}
            >
                <SmartRichTextView content={item.DocumentTitle.Text || "Untitled"} />
            </CrossInstanceLink>
            {renderExcerpt()}
            <PageSourceLink resultItem={item} instanceId={currentInstanceId} />
            {renderBadges()}
        </SpaceBetween>
    );
};
